import { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { useSnackbar } from 'notistack'

import Api from '../utils/api'
import { formatAlerts } from '../utils/helpers'

import { AuthenticationContext } from './auth-provider'

export const defaultDashboardContext = {
  selectedFolderId: null,
  folder: null,
  alerts: [],
  activeAlerts: [],
  resolvedAlerts: [],
  loading: true,
  error: '',
  refreshDevicesCounter: 0,
  setRefreshDevicesCounter: () => {},
}

export const DashboardContext = createContext<{
  selectedFolderId: number | null
  folder: IFolderType | null
  alerts: IAlertHistory[]
  activeAlerts: IAlertHistory[]
  resolvedAlerts: IAlertHistory[]
  loading: boolean
  error: string
  refreshDevicesCounter: number
  setRefreshDevicesCounter: React.Dispatch<React.SetStateAction<number>>
}>(defaultDashboardContext)

const DashboardProvider: FC<PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate()
  const { ownUser } = useContext(AuthenticationContext)
  const location = useLocation()
  const { folderId: folderIdParam } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const selectedFolderId = useMemo(() => {
    if (folderIdParam) {
      const parsedFolderId = Number(folderIdParam)
      if (!Number.isNaN(parsedFolderId)) {
        return parsedFolderId
      }
    }
    return null
  }, [folderIdParam])
  const [folder, setFolder] = useState<IFolderType | null>(null)
  const [alerts, setAlerts] = useState<IAlertHistory[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [refreshDevicesCounter, setRefreshDevicesCounter] = useState<number>(0)
  const [accessDeniedFolders, setAccessDeniedFolders] = useState<Set<number>>(new Set())
  const skipFolderAutoSelect = useMemo(() => new URLSearchParams(location.search).get('skipFolderSelection') === 'true', [location.search])

  const activeAlerts = useMemo(() => alerts.filter((alert) => alert.status === 'active'), [alerts])
  const resolvedAlerts = useMemo(() => alerts.filter((alert) => alert.status === 'resolved'), [alerts])

  // Keep dashboard URLs canonical so selected folder is always represented in the path.
  useEffect(() => {
    const isDashboardRoot = location.pathname === '/dashboard'
    const isDashboardFolderRoot = /^\/dashboard\/\d+$/.test(location.pathname)

    if (!isDashboardRoot && !isDashboardFolderRoot) {
      return
    }

    if (selectedFolderId) {
      navigate(`/dashboard/${selectedFolderId}/devices`, { replace: true })
      return
    }

    // If there is no folder selected but the user has one folder, auto-select it.
    // Only auto-select if we haven't previously been denied access to it.
    if (ownUser?.folders.length === 1 && !accessDeniedFolders.has(ownUser.folders[0]) && !skipFolderAutoSelect) {
      navigate(`/dashboard/${ownUser.folders[0]}/devices`, { replace: true })
    }
  }, [accessDeniedFolders, location.pathname, navigate, ownUser, selectedFolderId, skipFolderAutoSelect])

  useEffect(() => {
    if (!selectedFolderId || !ownUser) return
    // If the user doesn't have access to this folder, show an error and redirect
    // to /dashboard which will prompt them to select an accessible folder.
    if (!ownUser.folders.includes(selectedFolderId)) {
      if (!accessDeniedFolders.has(selectedFolderId)) {
        enqueueSnackbar('You do not have access to that location.', { variant: 'error' })
        setAccessDeniedFolders((prev) => new Set(prev).add(selectedFolderId))
      }
      navigate('/dashboard', { replace: true })
      return
    }
    Api.get(`/api/folders/${selectedFolderId}`).then((res) => {
      if (res.ok) {
        setFolder(res.body)
      } else if (res.status === 403 || res.status === 404) {
        if (!accessDeniedFolders.has(selectedFolderId)) {
          enqueueSnackbar('You do not have access to that location.', { variant: 'error', autoHideDuration: 10000 })
          setAccessDeniedFolders((prev) => new Set(prev).add(selectedFolderId))
        }
        navigate('/dashboard', { replace: true })
      } else {
        setError('Could not fetch folder information')
      }
    })
  }, [accessDeniedFolders, enqueueSnackbar, navigate, ownUser, selectedFolderId])

  useEffect(() => {
    const getAlerts = async () => {
      if (selectedFolderId && !accessDeniedFolders.has(selectedFolderId)) {
        setLoading(true)
        const alertsRes = await Api.get(`/api/v2/protect/folders/${selectedFolderId}/alerts`)
        if (alertsRes.ok) {
          setAlerts(formatAlerts(alertsRes.body.alerts))
        } else {
          setError('Error fetching alerts')
        }
      }
      setLoading(false)
    }

    getAlerts()
  }, [accessDeniedFolders, ownUser, selectedFolderId])

  useEffect(() => {
    const getAttributes = async () => {
      if (selectedFolderId && folder && !accessDeniedFolders.has(selectedFolderId)) {
        // If PA is not signed or needs re-signing, redirect to the participation agreement page
        const resAttributes = await Api.get(`/api/v2/folders/${selectedFolderId}/attributes`)
        if (resAttributes.ok) {
          let agreementNeedsSigning = false

          const resAgreement = await Api.get(`/api/v2/protect/participation-agreement/${selectedFolderId}`)
          if (resAgreement.ok) {
            // Check the latest agreement has been signed
            if (!resAgreement.body.latestAgreementAccepted) {
              agreementNeedsSigning = true
            }
          }

          // Check that the folder attributes indicate the agreement has been signed
          const { stepParticipationAgreementUser, participantAgreementDate, participantAgreementSignedByName, stepContactInformationUser } =
            resAttributes.body as IFolderAttributes
          if (!(stepParticipationAgreementUser > 0 && participantAgreementDate && participantAgreementSignedByName)) {
            agreementNeedsSigning = true
          }

          if (agreementNeedsSigning) navigate('/participation-agreement', { state: { folderId: selectedFolderId, fromDashboard: true } })
          // If the agreement is signed but contact information has not been set up, redirect to the onboarding contacts screen
          else if (stepContactInformationUser === 0) navigate('/onboarding-contacts', { state: { folderId: selectedFolderId } })
        } else {
          setError('Could not fetch folder attributes')
          setLoading(false)
        }
      }
    }

    getAttributes()
  }, [accessDeniedFolders, folder, navigate, selectedFolderId])

  return (
    <DashboardContext.Provider
      value={{
        selectedFolderId,
        folder,
        alerts,
        activeAlerts,
        resolvedAlerts,
        loading,
        error,
        refreshDevicesCounter,
        setRefreshDevicesCounter,
      }}
    >
      {children}
    </DashboardContext.Provider>
  )
}

export default DashboardProvider
